// Per-driver excess fund status -- reads the same drivers.insurance_start_date
// / insurance_last_claim_date fields the hand-built v_insurance_progress DB
// view already used, plus the new driver_excess_fund_ledger for a real
// running balance, via calculateFundStatus() so both this screen and the
// Incidents tab's charge preview agree.
//
// Also: the start date can be edited right here, "Backfill worked weeks" adds the weekly
// contribution for each week since a driver's start date that they worked (relief drivers get
// none for weeks with no work), and the No Claim Rebate (paid after 18 claim-free months) can be
// recorded with its date and amount -- the next is then due 18 months after it.
import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, Landmark, History } from "lucide-react";
import {
  DEFAULT_INSURANCE_EXCESS_SETTINGS,
  FundLedgerEntry,
  InsuranceExcessSettings,
  backfillExcessFund,
  calculateFundStatus,
  deleteNoClaimRebate,
  loadInsuranceExcessSettings,
  loadNoClaimRebates,
  recordNoClaimRebate,
  type ExcessBackfillResult,
  type NoClaimRebate,
} from "@/lib/insuranceExcess";
import { formatDate } from "@/lib/dates";
import { useAppContext } from "@/contexts/AppContext";
import { canEditTab } from "@/lib/permissions";

interface DriverFundRow {
  driver_name: string;
  insurance_start_date: string | null;
  insurance_last_claim_date: string | null;
  monthsContributing: number | null;
  currentBalance: number;
  claimableTier: number;
  eligibleForBonus: boolean;
  rebateDueOn: string | null;
  lastRebate: NoClaimRebate | null;
  reconciledCount: number;
  unreconciledCount: number;
}

const todayIso = () => new Date().toLocaleDateString("en-CA", { timeZone: "Pacific/Auckland" });

const InsuranceExcessFundTab: React.FC = () => {
  // A restricted user (see src/lib/permissions.ts, Settings -> Team
  // Access) sees only their linked driver(s) here, read-only unless
  // explicitly given edit rights. Unrestricted (the default -- everyone
  // today) is unaffected.
  const { effectivePermissions } = useAppContext();
  const canEdit = canEditTab(effectivePermissions, "insurance");
  const scopedDriverNames = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedDriverNames;

  const [rows, setRows] = useState<DriverFundRow[]>([]);
  const [settings, setSettings] = useState<InsuranceExcessSettings>(DEFAULT_INSURANCE_EXCESS_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [savingStartFor, setSavingStartFor] = useState<string | null>(null);

  // Backfill preview / confirm
  const [backfill, setBackfill] = useState<ExcessBackfillResult | null>(null);
  const [backfillBusy, setBackfillBusy] = useState(false);

  // Record a No Claim Rebate
  const [rebateFor, setRebateFor] = useState<string | null>(null);
  const [rebateDate, setRebateDate] = useState(todayIso());
  const [rebateAmount, setRebateAmount] = useState("");
  const [rebateNote, setRebateNote] = useState("");
  const [rebateSaving, setRebateSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    let driversQuery = supabase
      .from("drivers")
      .select("driver_name, insurance_start_date, insurance_last_claim_date")
      .eq("is_active", true)
      .order("driver_name");
    let ledgerQuery = supabase
      .from("driver_excess_fund_ledger")
      .select("driver_name, entry_type, amount, running_balance, created_at, bank_reconciled")
      .order("created_at", { ascending: true });
    if (scopedDriverNames) {
      const scoped = scopedDriverNames.length > 0 ? scopedDriverNames : ["__none__"];
      driversQuery = driversQuery.in("driver_name", scoped);
      ledgerQuery = ledgerQuery.in("driver_name", scoped);
    }
    const [loadedSettings, { data: drivers }, { data: ledger }, rebates] = await Promise.all([
      loadInsuranceExcessSettings(),
      driversQuery,
      ledgerQuery,
      loadNoClaimRebates(),
    ]);
    setSettings(loadedSettings);

    const lastRebateByDriver = new Map<string, NoClaimRebate>();
    rebates.forEach((r) => lastRebateByDriver.set(r.driver_name, r)); // ordered oldest -> newest

    const ledgerByDriver = new Map<string, FundLedgerEntry[]>();
    const reconciledByDriver = new Map<string, number>();
    const unreconciledByDriver = new Map<string, number>();
    (ledger || []).forEach((entry) => {
      const list = ledgerByDriver.get(entry.driver_name) || [];
      list.push(entry);
      ledgerByDriver.set(entry.driver_name, list);
      if (entry.entry_type === "contribution") {
        const map = entry.bank_reconciled ? reconciledByDriver : unreconciledByDriver;
        map.set(entry.driver_name, (map.get(entry.driver_name) || 0) + 1);
      }
    });

    const computed = (drivers || []).map((d) => {
      const lastRebate = lastRebateByDriver.get(d.driver_name) ?? null;
      const status = calculateFundStatus(
        { ...d, lastRebatePaidOn: lastRebate?.paid_on ?? null },
        ledgerByDriver.get(d.driver_name) || [],
        loadedSettings
      );
      return {
        driver_name: d.driver_name,
        insurance_start_date: d.insurance_start_date,
        insurance_last_claim_date: d.insurance_last_claim_date,
        reconciledCount: reconciledByDriver.get(d.driver_name) || 0,
        unreconciledCount: unreconciledByDriver.get(d.driver_name) || 0,
        lastRebate,
        ...status,
      };
    });
    setRows(computed);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedDriverNames]);

  const saveStartDate = async (driverName: string, value: string) => {
    if (!canEdit) return;
    setSavingStartFor(driverName);
    const { error } = await supabase
      .from("drivers")
      .update({ insurance_start_date: value || null })
      .eq("driver_name", driverName);
    setSavingStartFor(null);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Start date saved", description: value ? `${driverName} contributing since ${formatDate(value)}.` : `${driverName}'s start date cleared.` });
      load();
    }
  };

  const openBackfill = async () => {
    setBackfillBusy(true);
    try {
      setBackfill(await backfillExcessFund(false));
    } catch (e: any) {
      toast({ title: "Couldn't work out the backfill", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setBackfillBusy(false);
    }
  };

  const applyBackfill = async () => {
    setBackfillBusy(true);
    try {
      const res = await backfillExcessFund(true);
      toast({ title: "Excess fund backfilled", description: `${res.total_weeks} weekly contributions added.` });
      setBackfill(null);
      await load();
    } catch (e: any) {
      toast({ title: "Couldn't backfill", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setBackfillBusy(false);
    }
  };

  const openRebate = (driverName: string) => {
    setRebateFor(driverName);
    setRebateDate(todayIso());
    setRebateAmount(String(settings.noClaimBonusAmount));
    setRebateNote("");
  };

  const saveRebate = async () => {
    if (!rebateFor) return;
    const amount = Number(rebateAmount);
    if (!rebateDate || !Number.isFinite(amount) || amount < 0) {
      toast({ title: "Enter the date and amount paid", variant: "destructive" });
      return;
    }
    setRebateSaving(true);
    try {
      await recordNoClaimRebate({ driverName: rebateFor, paidOn: rebateDate, amount, note: rebateNote });
      toast({ title: "No Claim Rebate recorded", description: `${rebateFor} -- paid ${formatDate(rebateDate)}.` });
      setRebateFor(null);
      await load();
    } catch (e: any) {
      toast({ title: "Couldn't save", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setRebateSaving(false);
    }
  };

  const undoRebate = async (r: NoClaimRebate) => {
    if (!window.confirm(`Remove the No Claim Rebate paid to ${r.driver_name} on ${formatDate(r.paid_on)}?`)) return;
    try {
      await deleteNoClaimRebate(r.id);
      await load();
    } catch (e: any) {
      toast({ title: "Couldn't remove", description: e?.message ?? String(e), variant: "destructive" });
    }
  };

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xl text-primary flex items-center gap-2">
          <Landmark className="w-5 h-5" />
          Excess Fund
        </CardTitle>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button variant="outline" size="sm" onClick={openBackfill} disabled={loading || backfillBusy}>
              <History className="w-4 h-4 mr-2" />
              Backfill worked weeks
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Each driver's tenure and running balance in the ${settings.weeklyContribution}/week excess fund. A claim
          resets the tenure clock (handled automatically when a charge is confirmed on the Incidents tab). Set the
          <strong> Contributing Since </strong>date for each driver, then use <strong>Backfill worked weeks</strong> to add
          a contribution for every week since then that they worked (no work that week = no contribution).
        </p>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead>Contributing Since</TableHead>
                  <TableHead>Months</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead>Claimable Now</TableHead>
                  <TableHead>No Claim Rebate</TableHead>
                  <TableHead>Payments</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.driver_name}>
                    <TableCell className="font-medium">{row.driver_name}</TableCell>
                    <TableCell>
                      {canEdit ? (
                        <Input
                          type="date"
                          key={`${row.driver_name}-${row.insurance_start_date ?? ""}`}
                          defaultValue={row.insurance_start_date ? row.insurance_start_date.slice(0, 10) : ""}
                          disabled={savingStartFor === row.driver_name}
                          onBlur={(e) => {
                            if (e.target.value !== (row.insurance_start_date ? row.insurance_start_date.slice(0, 10) : "")) {
                              saveStartDate(row.driver_name, e.target.value);
                            }
                          }}
                          className="h-8 w-40"
                          aria-label={`Contributing since for ${row.driver_name}`}
                        />
                      ) : row.insurance_start_date ? (
                        formatDate(row.insurance_start_date)
                      ) : (
                        "--"
                      )}
                    </TableCell>
                    <TableCell>{row.monthsContributing ?? "--"}</TableCell>
                    <TableCell>${row.currentBalance.toFixed(2)}</TableCell>
                    <TableCell>${row.claimableTier.toFixed(2)}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {row.lastRebate && (
                          <div className="text-xs">
                            <Badge className="bg-blue-900 text-blue-200">
                              Paid {formatDate(row.lastRebate.paid_on)} ${row.lastRebate.amount.toFixed(2)}
                            </Badge>
                            {canEdit && (
                              <button type="button" className="ml-2 underline text-muted-foreground" onClick={() => undoRebate(row.lastRebate!)}>
                                undo
                              </button>
                            )}
                          </div>
                        )}
                        {row.eligibleForBonus ? (
                          <div className="flex items-center gap-2">
                            <Badge className="bg-green-800 text-green-200">Due now (${settings.noClaimBonusAmount})</Badge>
                            {canEdit && (
                              <Button size="sm" variant="outline" className="h-7" onClick={() => openRebate(row.driver_name)}>
                                Record paid
                              </Button>
                            )}
                          </div>
                        ) : row.rebateDueOn ? (
                          <span className="text-xs text-muted-foreground">Next due {formatDate(row.rebateDueOn)}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Set a start date</span>
                        )}
                        {!row.eligibleForBonus && row.rebateDueOn && canEdit && (
                          <button type="button" className="block text-xs underline text-muted-foreground" onClick={() => openRebate(row.driver_name)}>
                            record a payment
                          </button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {row.reconciledCount > 0 && (
                          <Badge className="bg-green-800 text-green-200">
                            {row.reconciledCount} reconciled
                          </Badge>
                        )}
                        {row.unreconciledCount > 0 && (
                          <Badge className="bg-orange-800 text-orange-200">
                            {row.unreconciledCount} unmatched
                          </Badge>
                        )}
                        {row.reconciledCount === 0 && row.unreconciledCount === 0 && (
                          <span className="text-xs text-muted-foreground">No payments yet</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Backfill preview */}
      <Dialog open={!!backfill} onOpenChange={(v) => !v && setBackfill(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Backfill worked weeks</DialogTitle>
          </DialogHeader>
          {backfill && (
            <div className="space-y-3 text-sm">
              {backfill.total_weeks === 0 ? (
                <p>
                  Nothing to add. Every driver with a Contributing Since date already has a contribution for each week they worked,
                  up to the last paid week{backfill.through ? ` (${formatDate(backfill.through)})` : ""}.
                </p>
              ) : (
                <>
                  <p>
                    This adds <strong>${backfill.weekly.toFixed(2)}</strong> for each week since the driver's start date that they had
                    completed jobs, up to the last paid week ({backfill.through ? formatDate(backfill.through) : "--"}). Weeks they didn't
                    work are skipped, and weeks already in the fund are never doubled up. Later weeks are added as you export each bank file.
                  </p>
                  <div className="border rounded overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Driver</TableHead>
                          <TableHead>Weeks</TableHead>
                          <TableHead>Adds</TableHead>
                          <TableHead>From</TableHead>
                          <TableHead>To</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {backfill.drivers.map((d) => (
                          <TableRow key={d.driver_name}>
                            <TableCell className="font-medium">{d.driver_name}</TableCell>
                            <TableCell>{d.weeks}</TableCell>
                            <TableCell>${Number(d.amount).toFixed(2)}</TableCell>
                            <TableCell>{formatDate(d.first_week)}</TableCell>
                            <TableCell>{formatDate(d.last_week)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <p className="text-muted-foreground">
                    {backfill.total_weeks} contributions, ${(backfill.total_weeks * backfill.weekly).toFixed(2)} in total. Drivers with no
                    Contributing Since date aren't included.
                  </p>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBackfill(null)}>
              {backfill && backfill.total_weeks === 0 ? "Close" : "Cancel"}
            </Button>
            {backfill && backfill.total_weeks > 0 && (
              <Button onClick={applyBackfill} disabled={backfillBusy}>
                {backfillBusy ? "Adding..." : `Add ${backfill.total_weeks} contributions`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record a No Claim Rebate */}
      <Dialog open={!!rebateFor} onOpenChange={(v) => !v && setRebateFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>No Claim Rebate — {rebateFor}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Date paid</Label>
              <Input type="date" value={rebateDate} onChange={(e) => setRebateDate(e.target.value)} />
            </div>
            <div>
              <Label>Amount ($)</Label>
              <Input type="number" min="0" step="0.01" value={rebateAmount} onChange={(e) => setRebateAmount(e.target.value)} />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input value={rebateNote} onChange={(e) => setRebateNote(e.target.value)} />
            </div>
            <p className="text-xs text-muted-foreground">
              Once recorded, the next rebate is due {settings.tenureWindowMonths} months after this date (a claim in between restarts the cycle).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRebateFor(null)}>
              Cancel
            </Button>
            <Button onClick={saveRebate} disabled={rebateSaving}>
              {rebateSaving ? "Saving..." : "Record rebate paid"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default InsuranceExcessFundTab;
